import {
  ListMask,
  useScrollableMask,
} from '@euler/app/components/list/ListMask';
import { ImageViewer } from '@euler/app/components/media/ImageViewer';
import { useMediaCarousel } from '@euler/app/components/media/MediaCarousel';
import { MediaCoverView } from '@euler/app/components/media/MediaCoverView';
import { Typography } from '@euler/app/components/typography';
import { ConstructionReportJobProductList } from '@euler/app/flows/report/components/construction/ConstructionReportJobProductList';
import { ProductManifestProps } from '@euler/app/flows/report/types';
import { Center } from '@euler/components/Center';
import { Colors } from '@euler/components/Colors';
import { HStack } from '@euler/components/layout/Stack';
import { computeGridLayoutItemSize } from '@euler/functions/layoutMetrics';
import { useResponsiveContext } from '@euler/functions/responsive/ResponsiveContext';
import { useIsLandscape } from '@euler/functions/responsiveness';
import { useContainerDimensions } from '@euler/functions/useAdaptiveContainerWidth';
import { MediaObject } from '@euler/model';
import { FaultInspectionItem } from '@euler/model/FaultInspectionItem';
import { InspectionTaskJobStatus } from '@euler/model/enum';
import { VehicleReport } from '@euler/model/report';
import { formatDateTime } from '@euler/utils/formatters';
import { memo, useCallback } from 'react';
import {
  FlatList,
  ListRenderItem,
  Platform,
  StyleProp,
  Text,
  TouchableOpacity,
  View,
  ViewStyle,
} from 'react-native';
import { Fill } from 'react-slot-fill';
import {
  ConstructionCellModel,
  useConstructionReportModels,
} from '../../functions/useConstructionReportModels';
import { JobDetailItemView } from './JobDetailItemView';
import { getConstructionReportItemStatusInfo } from './util';

export const ConstructionReportViewForIpad = memo(
  ({
    report,
    productManifest,
    targetJobId,
    faultLikeSubject,
  }: {
    report: VehicleReport;
    productManifest?: ProductManifestProps;
    targetJobId?: number;
    faultLikeSubject?: FaultInspectionItem;
  }) => {
    const { rpx } = useResponsiveContext();
    const { cells } = useConstructionReportModels(report, {
      targetJobId,
      faultLikeSubject,
    });
    const { width, height } = useContainerDimensions();
    const {
      showLeadingMask,
      showTrailingMask,
      onLayout,
      onScroll,
      onContentSizeChange,
    } = useScrollableMask({
      orientation: 'vertical',
    });

    const renderItem: ListRenderItem<ConstructionCellModel> = useCallback(
      ({ item }) => (
        <Cell model={item} width={width} productManifest={productManifest} />
      ),
      [productManifest, width],
    );
    return (
      <>
        <ListMask
          orientation="vertical"
          leadingMask={showLeadingMask}
          trailingMask={showTrailingMask}
          location={rpx(20) / height}
          css={`
            flex: 1;
          `}
        >
          <FlatList
            data={cells}
            renderItem={renderItem}
            onLayout={onLayout}
            onScroll={onScroll}
            onContentSizeChange={onContentSizeChange}
            scrollEventThrottle={1}
            showsVerticalScrollIndicator={Platform.OS === 'web'}
            contentContainerStyle={{
              paddingBottom: rpx(40),
            }}
          />
        </ListMask>
        <Fill name="reports/construction/page-container/footer/left">
          <Typography.Label light color={Colors.White5} size={rpx(18)}>
            施工技师: {report.construction?.technicianName ?? '-'}
          </Typography.Label>
          <Typography.Label light color={Colors.White5} size={rpx(18)}>
            {report.construction?.finishedAt != null
              ? '完成时间: '
              : '施工时间: '}
            {formatDateTime(
              report.construction?.finishedAt ??
                report.construction?.createdAt ??
                new Date(),
            )}
          </Typography.Label>
        </Fill>
      </>
    );
  },
);

const Cell = memo(
  ({
    model,
    style,
    width,
    productManifest,
  }: {
    model: ConstructionCellModel;
    style?: StyleProp<ViewStyle>;
    productManifest?: ProductManifestProps;
    width: number;
  }) => {
    const { rpx } = useResponsiveContext();
    return (
      <View
        css={`
          background-color: #fff;
          padding: ${rpx(20)}px;
          margin: ${rpx(10)}px ${rpx(20)}px;
          border-radius: ${rpx(20)}px;
        `}
        style={style}
      >
        <JobHeadView
          index={model.index}
          title={model.name}
          status={model.status}
          technicianName={model.technicianName}
          timestamp={model.timestamp}
          css={`
            overflow: hidden;
          `}
        />
        <JobDetailView
          model={model}
          width={width}
          productManifest={productManifest}
          css={`
            margin-top: ${rpx(16)}px;
          `}
        />
      </View>
    );
  },
);

const JobHeadView = memo(
  ({
    index,
    title,
    remark,
    status,
    technicianName,
    timestamp,
    style,
  }: {
    index: number;
    title: string;
    status: InspectionTaskJobStatus;
    remark?: string;
    technicianName: string;
    timestamp: string | Date;
    style?: StyleProp<ViewStyle>;
  }) => {
    const { rpx } = useResponsiveContext();
    const statusInfo = getConstructionReportItemStatusInfo(status);
    return (
      <View style={style}>
        <HStack justifyContent="flex-start" alignItems="center">
          <Text
            css={`
              font-size: ${rpx(40)}px;
              line-height: ${rpx(48)}px;
              font-style: italic;
              font-weight: bold;
              color: #666;
              top: ${rpx(-2)}px;
              margin-right: ${rpx(10)}px;
            `}
          >
            {`${index + 1}.`}
          </Text>
          <Typography.Label
            bold
            size={rpx(28)}
            color={'#666'}
            numberOfLines={1}
          >
            {title}
          </Typography.Label>
          <Center
            css={`
              border-width: 1px;
              border-color: ${statusInfo.color};
              margin-left: ${rpx(8)}px;
              padding: 0 ${rpx(10)}px;
              height: ${rpx(28)}px;
              border-radius: ${rpx(14)}px;
            `}
          >
            <Typography.Label
              color={statusInfo.color}
              regular
              size={rpx(15)}
              css={`
                text-align: center;
              `}
            >
              {statusInfo.text}
            </Typography.Label>
          </Center>
        </HStack>
        <HStack
          justifyContent="flex-start"
          css={`
            margin-top: ${rpx(5)}px;
          `}
        >
          <Typography.Label
            light
            size={rpx(16)}
            numberOfLines={1}
            color={'#444'}
          >
            施工技师: {technicianName}
          </Typography.Label>
          <Typography.Label
            light
            size={rpx(16)}
            numberOfLines={1}
            color={'#444'}
            css={`
              margin-left: ${rpx(16)}px;
            `}
          >
            施工时间: {formatDateTime(timestamp)}
          </Typography.Label>
        </HStack>
        {remark ? (
          <Typography.Label
            thin
            size={rpx(14)}
            color="#666"
            css={`
              margin-top: ${rpx(15)}px;
            `}
          >
            {remark}
          </Typography.Label>
        ) : null}
      </View>
    );
  },
);

const JobDetailView = memo(
  ({
    model,
    width,
    productManifest,
    style,
  }: {
    model: ConstructionCellModel;
    width: number;
    productManifest?: ProductManifestProps;
    style?: StyleProp<ViewStyle>;
  }) => {
    const { rpx } = useResponsiveContext();
    const medias = model.procedures.flatMap(x => x.medias);
    const isLandscape = useIsLandscape();
    const columns = isLandscape ? 3.75 : 2.75;
    const [imageWidth, imageHeight] = computeGridLayoutItemSize({
      numberOfColumns: columns,
      gridWidth: width,
      cellMargin: { left: 0, top: 0, bottom: 0, right: rpx(kCellMargin) },
      aspectRatio: 5 / 4,
      gridPadding: 0,
    });

    return (
      <View style={style}>
        {model.details.map((detail, index) => (
          <JobDetailItemView
            key={index}
            title={detail.title}
            status={model.status}
            defectiveLevel={detail.defectiveLevel}
            titleStyle={{ fontSize: rpx(15), lineHeight: rpx(24) }}
            statusStyle={{ fontSize: rpx(15), lineHeight: rpx(24) }}
            iconSize={
              model.status === InspectionTaskJobStatus.Finished
                ? rpx(15)
                : rpx(13)
            }
            iconStyle={{ top: 0, marginRight: rpx(4) }}
          />
        ))}
        <ConstructionReportJobProductList
          productManifest={productManifest}
          jobId={model.jobId}
          jobName={model.name}
          productConfirmations={model.productConfirmations}
          css={`
            margin-top: ${rpx(16)}px;
          `}
        />
        <MediaListView
          medias={medias}
          width={width}
          imageWidth={imageWidth}
          imageHeight={imageHeight}
          css={`
            margin-top: ${rpx(16)}px;
            height: ${imageHeight}px;
          `}
        />
      </View>
    );
  },
);

const kCellMargin = 20;

const MediaListView = memo(
  ({
    medias,
    width,
    imageWidth,
    imageHeight,
    style,
  }: {
    medias: MediaObject[];
    width: number;
    imageWidth: number;
    imageHeight: number;
    style?: StyleProp<ViewStyle>;
  }) => {
    const { rpx } = useResponsiveContext();
    const {
      showLeadingMask,
      showTrailingMask,
      onScroll,
      onContentSizeChange,
      onLayout,
    } = useScrollableMask({ orientation: 'horizontal' });

    const {
      isImageViewerOpen,
      setIsImageViewerOpen,
      imageUrls,
      currentImageIndex,
      onMediaPress,
    } = useMediaCarousel(medias);

    const renderItem: ListRenderItem<MediaObject> = useCallback(
      ({ item, index }) => {
        return (
          <MediaCell
            media={item}
            index={index}
            onPress={onMediaPress}
            imageWidth={imageWidth}
            imageHeight={imageHeight}
            css={`
              margin-right: ${rpx(kCellMargin)}px;
            `}
          />
        );
      },
      [imageHeight, imageWidth, onMediaPress, rpx],
    );

    if (!medias.length) {
      return (
        <Center
          css={`
            background-color: #eee;
            border-radius: ${rpx(10)}px;
            width: ${imageWidth}px;
            height: ${imageHeight}px;
          `}
          style={style}
        >
          <Typography.Label size={rpx(16)} color={'#555'} light>
            暂无施工图片/视频
          </Typography.Label>
        </Center>
      );
    }

    return (
      <ListMask
        leadingMask={showLeadingMask}
        trailingMask={showTrailingMask}
        style={style}
        location={rpx(15) / width}
      >
        <ImageViewer
          images={imageUrls}
          index={currentImageIndex}
          show={isImageViewerOpen}
          onDismiss={() => setIsImageViewerOpen(false)}
        />
        <FlatList
          data={medias}
          keyExtractor={x => x.id}
          horizontal
          showsHorizontalScrollIndicator={Platform.OS === 'web'}
          scrollEventThrottle={1}
          onScroll={onScroll}
          onLayout={onLayout}
          onContentSizeChange={onContentSizeChange}
          renderItem={renderItem}
          contentContainerStyle={{
            flexGrow: 1,
            alignItems: 'stretch',
          }}
        />
      </ListMask>
    );
  },
);

const MediaCell = memo(
  ({
    media,
    style,
    mediaStyle,
    index,
    imageWidth,
    imageHeight,
    onPress,
  }: {
    media: MediaObject;
    index: number;
    style?: StyleProp<ViewStyle>;
    mediaStyle?: StyleProp<ViewStyle>;
    imageWidth: number;
    imageHeight: number;
    onPress?: (media: MediaObject, index: number) => void;
  }) => {
    const { rpx } = useResponsiveContext();

    const handlePress = useCallback(() => {
      onPress?.(media, index);
    }, [media, index, onPress]);

    const title = media.subTitle ?? media.title;

    return (
      <View style={style}>
        <View
          style={mediaStyle}
          css={`
            width: ${imageWidth}px;
            height: ${imageHeight}px;
          `}
        >
          <MediaCoverView
            media={media}
            TouchableComponent={TouchableOpacity}
            onPress={handlePress}
            activityIndicatorSize={rpx(30)}
            noPlaceholder={
              media.type === 'video' ||
              (media.type === 'image' && media.url.includes('#'))
            }
            playIconSize={rpx(30)}
            playIconColor={Colors.White7_5}
            containerStyle={{
              borderRadius: rpx(10),
              overflow: 'hidden',
              width: imageWidth,
            }}
          />
        </View>
        <View
          pointerEvents="none"
          css={`
            margin-top: ${rpx(6)}px;
          `}
        >
          <Typography.Label
            regular
            size={rpx(13)}
            lineHeight={rpx(16)}
            color={'#666'}
            numberOfLines={1}
            css={`
              text-align: center;
            `}
          >
            {title ?? ''}
          </Typography.Label>
        </View>
      </View>
    );
  },
);
